import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronLeft, Loader2, Pencil } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StatusPill, type StatusPillStatus } from '@/components/ui/status-pill';
import { useSession } from '@/hooks/useSession';
import { cn } from '@/lib/utils';
import { patchArtifact, type Artifact, type ArtifactType, type PatchArtifactBody } from '@/lib/api';

/** Maps an artifact's lifecycle status to the closest StatusPill variant. */
const STATUS_TO_PILL: Record<Artifact['status'], StatusPillStatus> = {
  draft: 'draft',
  review: 'paused',
  approved: 'completed',
  superseded: 'cancelled',
  archived: 'archived',
};

const TEMPLATE_SUGGESTIONS: Record<ArtifactType, string[]> = {
  issue: ['feature', 'bug'],
  spec: ['default'],
  adr: ['default'],
  implementation_plan: ['default'],
  note: ['meeting', 'research', 'investigation', 'design-scratchpad'],
};

interface ArtifactHeaderBarProps {
  artifact: Artifact;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function emptyToNull(value: string): string | null {
  return value.trim() === '' ? null : value.trim();
}

function useSaveState(): {
  state: SaveState;
  message: string | null;
  begin: () => void;
  succeed: () => void;
  fail: (msg: string) => void;
} {
  const [state, setState] = useState<SaveState>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return (): void => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  return {
    state,
    message,
    begin: (): void => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      setMessage(null);
      setState('saving');
    },
    succeed: (): void => {
      setState('saved');
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setState('idle');
      }, 1500);
    },
    fail: (msg: string): void => {
      setState('error');
      setMessage(msg);
    },
  };
}

function SaveIndicator({
  state,
  message,
}: {
  state: SaveState;
  message: string | null;
}): React.ReactElement | null {
  if (state === 'idle') return null;
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
        <Loader2 className="size-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <Check className="size-3" /> Saved
      </span>
    );
  }
  return (
    <span role="alert" className="text-xs text-error">
      {message ?? 'Save failed'}
    </span>
  );
}

export function ArtifactHeaderBar({ artifact }: ArtifactHeaderBarProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useSession();
  const isOwnerOrAdmin = isAdmin || (user !== null && user.id === artifact.ownerId);
  const updated = artifact.updatedAt.slice(0, 19).replace('T', ' ');

  // Title state
  const [titleEditing, setTitleEditing] = useState(false);
  const [title, setTitle] = useState(artifact.title);
  const titleSave = useSaveState();

  // Summary state
  const [summaryEditing, setSummaryEditing] = useState(false);
  const [summary, setSummary] = useState(artifact.summary);
  const summarySave = useSaveState();

  // Template badges state
  const templateKeySave = useSaveState();
  const templateVersionSave = useSaveState();

  useEffect(() => {
    setTitle(artifact.title);
    setSummary(artifact.summary);
  }, [artifact.id, artifact.title, artifact.summary]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
    void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
    void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
  };

  const titleMutation = useMutation({
    mutationFn: (body: PatchArtifactBody) => patchArtifact(artifact.id, body),
    onSuccess: () => {
      invalidate();
      titleSave.succeed();
    },
    onError: err => {
      setTitle(artifact.title);
      titleSave.fail(err.message);
    },
  });

  const summaryMutation = useMutation({
    mutationFn: (body: PatchArtifactBody) => patchArtifact(artifact.id, body),
    onSuccess: () => {
      invalidate();
      summarySave.succeed();
    },
    onError: err => {
      setSummary(artifact.summary);
      summarySave.fail(err.message);
    },
  });

  const templateKeyMutation = useMutation({
    mutationFn: (body: PatchArtifactBody) => patchArtifact(artifact.id, body),
    onSuccess: () => {
      invalidate();
      templateKeySave.succeed();
    },
    onError: err => {
      templateKeySave.fail(err.message);
    },
  });

  const templateVersionMutation = useMutation({
    mutationFn: (body: PatchArtifactBody) => patchArtifact(artifact.id, body),
    onSuccess: () => {
      invalidate();
      templateVersionSave.succeed();
    },
    onError: err => {
      templateVersionSave.fail(err.message);
    },
  });

  function commitTitle(): void {
    setTitleEditing(false);
    const next = title.trim();
    if (next === '' || next === artifact.title) {
      setTitle(artifact.title);
      return;
    }
    titleSave.begin();
    titleMutation.mutate({ title: next });
  }

  function commitSummary(): void {
    setSummaryEditing(false);
    if (summary === artifact.summary) return;
    summarySave.begin();
    summaryMutation.mutate({ summary });
  }

  function commitTemplateKey(next: string): void {
    const value = emptyToNull(next);
    if (value === artifact.templateKey) return;
    templateKeySave.begin();
    templateKeyMutation.mutate({ templateKey: value });
  }

  function commitTemplateVersion(next: string): void {
    const value = emptyToNull(next);
    if (value === artifact.templateVersion) return;
    templateVersionSave.begin();
    templateVersionMutation.mutate({ templateVersion: value });
  }

  // Some types (e.g. legacy 'rca') aren't in TEMPLATE_SUGGESTIONS; default to []
  // so downstream `suggestions[0]` lookups don't throw.
  const suggestions = TEMPLATE_SUGGESTIONS[artifact.type] ?? [];

  const ownerLabel = artifact.ownerLogin ?? artifact.ownerId ?? '—';
  const isAgentOwner = ownerLabel.toLowerCase() === 'agent';
  const summaryEmpty = artifact.summary === '';
  const showSecondary = !summaryEmpty || isOwnerOrAdmin;

  return (
    <div className="relative flex shrink-0 flex-col gap-2 border-b border-border bg-surface/30 px-5 py-3 backdrop-blur-sm">
      {/* Row 1 — Back + Title + Status pill + Save indicator */}
      <div className="flex items-center gap-2.5">
        <Link
          to="/artifacts"
          className="inline-flex size-7 items-center justify-center rounded-md border border-border bg-surface-elevated text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
          aria-label="Back to artifacts"
        >
          <ChevronLeft className="size-3.5" />
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {titleEditing && isOwnerOrAdmin ? (
            <input
              autoFocus
              type="text"
              value={title}
              onChange={e => {
                setTitle(e.target.value);
              }}
              onBlur={commitTitle}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.currentTarget as HTMLInputElement).blur();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setTitle(artifact.title);
                  setTitleEditing(false);
                }
              }}
              aria-label="Title"
              className="w-full rounded-md border border-border bg-surface px-2 py-0.5 text-[15px] font-semibold tracking-tight text-text-primary outline-none ring-0 focus:border-primary"
            />
          ) : (
            <h1
              tabIndex={isOwnerOrAdmin ? 0 : -1}
              onClick={() => {
                if (isOwnerOrAdmin) setTitleEditing(true);
              }}
              onFocus={() => {
                if (isOwnerOrAdmin) setTitleEditing(true);
              }}
              className={cn(
                'truncate rounded-md px-1.5 py-0.5 text-[15px] font-semibold tracking-tight text-text-primary',
                isOwnerOrAdmin && 'cursor-text hover:bg-surface-elevated/60'
              )}
            >
              {artifact.title}
            </h1>
          )}
          <StatusPill status={STATUS_TO_PILL[artifact.status]}>{artifact.status}</StatusPill>
          <SaveIndicator state={titleSave.state} message={titleSave.message} />
        </div>
      </div>

      {/* Row 2 — mono meta line: type · scope · authored by · updated · slug */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-9 font-mono text-[10.5px] text-text-tertiary">
        <span>{artifact.type}</span>
        <span>·</span>
        <span>{artifact.scope}</span>
        <span>·</span>
        <span>
          authored by{' '}
          <span className={isAgentOwner ? 'text-accent-bright' : 'text-text-secondary'}>
            {ownerLabel}
          </span>
        </span>
        <span>·</span>
        <span>updated {updated}</span>
        <span>·</span>
        <span>{artifact.slug}</span>
        {artifact.visibility === 'private' && (
          <>
            <span>·</span>
            <span className="text-warning">private</span>
          </>
        )}
        {artifact.status === 'draft' && artifact.currentApprovedSnapshotId !== null && (
          <span
            className="ml-2 rounded border border-warning/25 bg-[oklch(0.78_0.16_75/0.12)] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-warning"
            title="The live document has been edited since the last approved snapshot."
          >
            Unapproved edits
          </span>
        )}
      </div>

      {/* Secondary row — summary + template badges. Kept compact so the main
       * header stays close to the design's two-line layout, but editing
       * affordances remain in place. Hidden entirely when there's nothing to
       * show and the user can't edit. */}
      {showSecondary && (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1 pl-9">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {summaryEditing && isOwnerOrAdmin ? (
              <textarea
                autoFocus
                value={summary}
                rows={2}
                onChange={e => {
                  setSummary(e.target.value);
                }}
                onBlur={commitSummary}
                aria-label="Summary"
                placeholder="Add a summary…"
                className="w-full resize-none rounded-md border border-border bg-surface px-2 py-1 text-[12px] text-text-secondary outline-none focus:border-primary"
              />
            ) : (
              <p
                tabIndex={isOwnerOrAdmin ? 0 : -1}
                onClick={() => {
                  if (isOwnerOrAdmin) setSummaryEditing(true);
                }}
                onFocus={() => {
                  if (isOwnerOrAdmin) setSummaryEditing(true);
                }}
                className={cn(
                  'min-h-[1.25rem] flex-1 truncate rounded-md px-1.5 py-0.5 text-[12px]',
                  summaryEmpty ? 'italic text-text-tertiary' : 'text-text-secondary',
                  isOwnerOrAdmin && 'cursor-text hover:bg-surface-elevated/60'
                )}
              >
                {summaryEmpty ? 'Add a summary…' : artifact.summary}
              </p>
            )}
            <SaveIndicator state={summarySave.state} message={summarySave.message} />
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <TemplateBadge
              label="Template"
              value={artifact.templateKey}
              suggestions={suggestions}
              editable={isOwnerOrAdmin}
              onSave={commitTemplateKey}
              saveState={templateKeySave.state}
              saveMessage={templateKeySave.message}
              placeholder={suggestions[0] ?? 'template'}
              inputAriaLabel="Template key"
            />
            <TemplateBadge
              label="v"
              value={artifact.templateVersion}
              suggestions={[]}
              editable={isOwnerOrAdmin}
              onSave={commitTemplateVersion}
              saveState={templateVersionSave.state}
              saveMessage={templateVersionSave.message}
              placeholder="1.0.0"
              inputAriaLabel="Template version"
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface TemplateBadgeProps {
  label: string;
  value: string | null;
  suggestions: string[];
  editable: boolean;
  onSave: (value: string) => void;
  saveState: SaveState;
  saveMessage: string | null;
  placeholder: string;
  inputAriaLabel: string;
}

function TemplateBadge({
  label,
  value,
  suggestions,
  editable,
  onSave,
  saveState,
  saveMessage,
  placeholder,
  inputAriaLabel,
}: TemplateBadgeProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const listId = `template-suggestions-${inputAriaLabel.replace(/\s+/g, '-').toLowerCase()}`;

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const isEmpty = value === null || value === '';
  const display = isEmpty ? (
    <span className="inline-flex items-center gap-1 italic text-text-secondary/70">
      <Pencil className="size-3" /> Add {label.toLowerCase()}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono text-text-secondary">{label}:</span>
      <span className="font-mono text-text-primary">{value}</span>
    </span>
  );

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'border-border bg-surface-elevated/60 font-normal text-text-primary',
        editable && 'cursor-pointer hover:border-primary/60 hover:bg-surface-elevated',
        isEmpty && 'border-dashed'
      )}
    >
      {display}
    </Badge>
  );

  if (!editable) {
    if (isEmpty) return <></>;
    return badge;
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Edit ${inputAriaLabel.toLowerCase()}`}
            className="inline-flex"
          >
            {badge}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <label className="flex flex-col gap-2 text-xs">
            <span className="font-medium text-text-secondary">{inputAriaLabel}</span>
            <input
              autoFocus
              type="text"
              value={draft}
              list={suggestions.length > 0 ? listId : undefined}
              placeholder={placeholder}
              aria-label={inputAriaLabel}
              onChange={e => {
                setDraft(e.target.value);
              }}
              onBlur={() => {
                onSave(draft);
                setOpen(false);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.currentTarget as HTMLInputElement).blur();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setDraft(value ?? '');
                  setOpen(false);
                }
              }}
              className="rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary outline-none focus:border-primary"
            />
            {suggestions.length > 0 && (
              <datalist id={listId}>
                {suggestions.map(s => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            )}
            <span className="text-text-secondary/70">Press Enter to save, Esc to cancel.</span>
          </label>
        </PopoverContent>
      </Popover>
      <SaveIndicator state={saveState} message={saveMessage} />
    </span>
  );
}
